import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthProvider';
import { checkUserAccess, AccessResponse, ApiResponse } from '../services/apiService';

export interface UseApiAuthResult {
  isLoading: boolean;
  accessResponse: ApiResponse<AccessResponse> | null;
  error: string | null;
  checkAccess: () => Promise<void>;
  resetApiCall: () => void; // Add reset function
}

/**
 * Custom hook to handle API operations for user access checking
 */
export const useApiAuth = (): UseApiAuthResult => {
  const { user, isAuthenticated } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [accessResponse, setAccessResponse] = useState<ApiResponse<AccessResponse> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasCalledApi, setHasCalledApi] = useState(false); // Track if API was already called
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts

  // Check user access via API
  const checkAccess = useCallback(async (): Promise<void> => {
    // Prevent duplicate calls
    if (isLoading || hasCalledApi) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasCalledApi(true);

    try {
      // API call with automatic token refresh handled by the API client
      const response = await checkUserAccess();
      setAccessResponse(response);
      
      if (!response.success) {
        console.error('[useApiAuth] Access check failed:', response.error);
        setError(response.error || 'Failed to check user access');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('[useApiAuth] Exception during access check:', errorMessage);
      setError(errorMessage);
      
      // Check if it's an authentication configuration error and retry automatically
      if (errorMessage.includes('API client not configured with authentication') && retryCount < 3) {
        console.warn(`[useApiAuth] Auth config error, retrying (${retryCount + 1}/3)`);
        setRetryCount(prev => prev + 1);
        setHasCalledApi(false);
        
        // Retry with increasing delay
        setTimeout(() => {
          checkAccess();
        }, 1000 * (retryCount + 1));
      } else {
        setHasCalledApi(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasCalledApi, retryCount]);

  // Function to reset API call state (allows retry)
  const resetApiCall = (): void => {
    setHasCalledApi(false);
    setAccessResponse(null);
    setError(null);
    setRetryCount(0);
  };

  // Auto-trigger API call when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user?.profile && user?.access_token && !hasCalledApi && !isLoading) {
      // Add delay to ensure API client is configured first
      const timeoutId = setTimeout(() => {
        checkAccess();
      }, 500);
      
      // Cleanup timeout if component unmounts or dependencies change
      return () => {
        clearTimeout(timeoutId);
      };
    }
    // Note: checkAccess is intentionally omitted from deps to prevent re-runs on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.profile, user?.access_token, hasCalledApi, isLoading]);

  return {
    isLoading,
    accessResponse,
    error,
    checkAccess,
    resetApiCall
  };
};
